import { type HapticKind, haptic } from './native.ts';
import { getSettings } from './settings.ts';

// Tiny synthesized sounds, so there are no audio files to ship.
let ctx: AudioContext | null = null;

function tone(freq: number, duration: number, type: OscillatorType, gain: number, delay = 0, slide = 0) {
  if (!getSettings().sound) return;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slide) osc.frequency.exponentialRampToValueAtTime(freq * slide, t0 + duration);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  } catch {
    // Audio is optional.
  }
}

const effects = {
  move: () => tone(420, 0.07, 'triangle', 0.25),
  capture: () => {
    tone(300, 0.09, 'square', 0.12);
    tone(180, 0.12, 'triangle', 0.2, 0.03);
  },
  check: () => tone(660, 0.15, 'triangle', 0.22),
  danger: () => {
    tone(520, 0.12, 'triangle', 0.2);
    tone(390, 0.18, 'triangle', 0.2, 0.12);
  },
  crawl: () => tone(150, 0.45, 'sine', 0.3, 0.25, 0.6),
  eat: () => {
    tone(160, 0.3, 'sine', 0.35, 0.25, 0.5);
    tone(90, 0.35, 'triangle', 0.25, 0.4);
  },
  boom: () => {
    tone(120, 0.35, 'sawtooth', 0.18, 0, 0.3);
    tone(70, 0.4, 'triangle', 0.3, 0.05, 0.5);
  },
  win: () => {
    tone(523, 0.18, 'triangle', 0.22);
    tone(659, 0.18, 'triangle', 0.22, 0.15);
    tone(784, 0.3, 'triangle', 0.22, 0.3);
  },
  lose: () => {
    tone(392, 0.2, 'triangle', 0.2);
    tone(330, 0.22, 'triangle', 0.2, 0.18);
    tone(262, 0.35, 'triangle', 0.2, 0.36);
  },
  start: () => {
    tone(392, 0.12, 'triangle', 0.2);
    tone(523, 0.18, 'triangle', 0.2, 0.1);
  },
  notify: () => tone(880, 0.12, 'sine', 0.18),
  honk: () => {
    tone(330, 0.12, 'sawtooth', 0.12, 0, 1.25);
    tone(300, 0.16, 'sawtooth', 0.12, 0.14, 1.2);
  },
  boing: () => {
    tone(260, 0.16, 'sine', 0.28, 0, 2.2);
    tone(520, 0.12, 'triangle', 0.12, 0.12, 0.7);
  },
  freeze: () => {
    tone(1568, 0.25, 'sine', 0.1);
    tone(2093, 0.3, 'sine', 0.08, 0.06);
  },
  thaw: () => tone(700, 0.22, 'sine', 0.14, 0, 0.6),
  hit: () => {
    tone(110, 0.14, 'square', 0.14, 0, 0.6);
    tone(220, 0.08, 'triangle', 0.18);
  },
  sizzle: () => {
    tone(900, 0.25, 'sawtooth', 0.05, 0, 0.4);
    tone(180, 0.3, 'triangle', 0.14, 0.02, 0.6);
  },
  levelup: () => {
    tone(523, 0.08, 'square', 0.1);
    tone(659, 0.08, 'square', 0.1, 0.08);
    tone(784, 0.08, 'square', 0.1, 0.16);
    tone(1047, 0.16, 'square', 0.1, 0.24);
  },
  warp: () => {
    tone(300, 0.3, 'sine', 0.2, 0, 3);
    tone(900, 0.2, 'triangle', 0.08, 0.2, 0.5);
  },
  rise: () => {
    tone(110, 0.5, 'sawtooth', 0.12, 0, 0.8);
    tone(140, 0.5, 'triangle', 0.18, 0.1, 0.7);
  },
  betray: () => {
    tone(700, 0.1, 'triangle', 0.18);
    tone(470, 0.25, 'sawtooth', 0.1, 0.1, 0.8);
  },
  flip: () => {
    tone(500, 0.06, 'triangle', 0.2);
    tone(750, 0.1, 'triangle', 0.2, 0.06);
  },
  rumble: () => {
    tone(60, 0.7, 'sawtooth', 0.16, 0, 0.7);
    tone(45, 0.8, 'triangle', 0.28, 0.1, 0.8);
  },
};

export type SoundName = keyof typeof effects;

/** In the app, the phone also taps for the moves that matter (even with sound off). */
const HAPTICS: Partial<Record<SoundName, HapticKind>> = {
  move: 'light',
  capture: 'medium',
  check: 'medium',
  danger: 'warning',
  eat: 'heavy',
  boom: 'heavy',
  hit: 'medium',
  flip: 'medium',
  betray: 'warning',
  rumble: 'heavy',
  win: 'success',
  lose: 'error',
  start: 'medium',
};

export const sounds = Object.fromEntries(
  (Object.keys(effects) as SoundName[]).map((name) => [
    name,
    () => {
      effects[name]();
      const h = HAPTICS[name];
      if (h) haptic(h);
    },
  ]),
) as Record<SoundName, () => void>;
